import React, { useContext, useState, useRef } from 'react';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { useMutation } from '@tanstack/react-query';
import { DocumentService } from '@dealstack/frontend/shared/dataExchange/services/document/document.service';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { PdfViewerModal } from '@dealstack/frontend/shared/components/DocumentViewerV2/PdfViewer/PdfViewerModal';
import {
	FileInfoContainer,
	FileName,
	FilePreviewContainer,
	TrashButton,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Holdings/HoldingEventDrawer/styled.tsx';
import { DealContext } from '@dealstack/frontend/app/pages/Project/DealDashboard/DealContext';

interface Props {
	fileName?: string;
	onRemove: () => void;
	fileId?: string;
	setIsDrawerVisible?: (isVisible: boolean) => void;
}

export const FilePreviewComponent = ({ fileName, onRemove, fileId, setIsDrawerVisible }: Props) => {
	const { dealId } = useContext(DealContext);
	const tCapManage = useTranslationCapManage();
	const [modalOpen, setModalOpen] = useState(false);
	const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);
	const pdfViewerRef = useRef(null);

	const getDownloadPreSignedUrl = useMutation({
		mutationFn: DocumentService.getDownloadPreSignedUrl,
	});

	const handlePreview = (e) => {
		e.preventDefault();
		if (!fileId || !dealId) {
			return;
		}

		if (setIsDrawerVisible) {
			setIsDrawerVisible(false);
		}

		getDownloadPreSignedUrl.mutate(
			{
				dealId,
				fileId: fileId || '',
			},
			{
				onSuccess: async (data) => {
					if (data.payload?.downloadUrl) {
						try {
							const response = await fetch(data.payload.downloadUrl);
							const blob = await response.blob();

							setPdfBlob(blob);
							setModalOpen(true);
						} catch {
							showToastV2({
								title: tCapManage('holdings.event.document_error'),
								state: 'error',
							});
						}
					}
				},
			}
		);
	};

	const handleCloseModal = () => {
		setModalOpen(false);
		setPdfBlob(null);

		if (setIsDrawerVisible) {
			setIsDrawerVisible(true);
		}
	};

	return (
		<>
			<FilePreviewContainer>
				<FileInfoContainer>
					<Icon variant="holdings-checkmark" size="xl" />
					<FileName
						onClick={handlePreview}
						style={{
							cursor: fileId ? 'pointer' : 'default',
						}}
					>
						{fileName}
					</FileName>
				</FileInfoContainer>
				<TrashButton onClick={onRemove}>
					<Icon variant="trash-can" size="lg" />
				</TrashButton>
			</FilePreviewContainer>

			<PdfViewerModal
				ref={pdfViewerRef}
				opened={modalOpen}
				onClose={handleCloseModal}
				pdfViewerProps={{
					fileName: fileName || 'Document',
					file: pdfBlob || undefined,
					isLoading: getDownloadPreSignedUrl.isPending,
				}}
			/>
		</>
	);
};

export const FilePreview = ({ fileName, onRemove }) => {
	return (
		<FilePreviewContainer>
			<FileInfoContainer>
				<Icon variant="holdings-checkmark" size="xl" />
				<FileName>{fileName}</FileName>
			</FileInfoContainer>
			<TrashButton onClick={onRemove}>
				<Icon variant="trash-can" size="lg" />
			</TrashButton>
		</FilePreviewContainer>
	);
};
